import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'

import { useDashboardFeatureFlags } from '@components'
import { Button, ModalBox, Tabs, Tooltip, useModalContext } from '@fourthwall/components'

import type { AppSection } from '../../types'
import { BasicTab } from './BasicTab'
import * as S from './EditSectionsModal.styled'
import { PreviewTab } from './PreviewTab'
import { SettingsTab } from './SettingsTab/SettingsTab'

type EditSectionsModalProps = {
  title: string
  section: AppSection
  onSave?: (section: AppSection) => void
  hmac: string
}

type SectionFormValues = {
  name: string
  description: string
  url: string
  settings: AppSection['settings']
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Section name is required'),
  description: Yup.string(),
  url: Yup.string().required('Iframe URL is required').url('Please enter a valid URL'),
})

const getInitialValues = (section: AppSection): SectionFormValues => ({
  name: section.name || '',
  description: section.description || '',
  url: section.iframeUrl || '',
  settings: section.settings || [],
})

export const EditSectionsModal = ({ title, hmac, section, onSave }: EditSectionsModalProps) => {
  const { close } = useModalContext()
  const [activeTab, setActiveTab] = useState(0)

  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const handleSubmit = (values: SectionFormValues) => {
    if (onSave) {
      const updatedSection: AppSection = {
        ...section,
        name: values.name.trim(),
        description: values.description.trim(),
        iframeUrl: values.url.trim(),
        settings: values.settings,
      }
      onSave(updatedSection)
    }
    close()
  }

  const handleCancel = () => {
    close()
  }

  return (
    <Formik<SectionFormValues>
      initialValues={getInitialValues(section)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ submitForm, values, setFieldValue }) => (
        <ModalBox
          title={title}
          onClose={handleCancel}
          buttons={[
            <Button label="Cancel" appearance="secondary" onClick={handleCancel} />,
            <Button label="Save" appearance="primary" onClick={submitForm} />,
          ]}
          size="large"
        >
          <S.Content>
            <Form>
              <Tabs defaultIndex={activeTab} onSelect={(index) => setActiveTab(index)}>
                <Tabs.List>
                  <Tabs.Item name="Basic Information" />
                  {dashboardFeatureFlags.developersSectionSettings.enabled && (
                    <Tabs.Item name="Settings" />
                  )}
                  {dashboardFeatureFlags.developersSectionSettings.enabled && (
                    <Tabs.Item name="Preview" />
                  )}
                  {!dashboardFeatureFlags.developersSectionSettings.enabled && (
                    <Tooltip
                      reference={<S.DisabledSettingsTooltip>Settings</S.DisabledSettingsTooltip>}
                    >
                      Customizing section settings is in beta. Message @jierenchen on Discord to
                      access.
                    </Tooltip>
                  )}
                </Tabs.List>

                <Tabs.Panel>
                  <BasicTab />
                </Tabs.Panel>

                {dashboardFeatureFlags.developersSectionSettings.enabled && (
                  <Tabs.Panel>
                    <SettingsTab
                      settings={values.settings}
                      onSettingsChange={(settings) => setFieldValue('settings', settings)}
                    />
                  </Tabs.Panel>
                )}

                {dashboardFeatureFlags.developersSectionSettings.enabled && (
                  <Tabs.Panel>
                    <PreviewTab settings={values.settings} iframeUrl={values.url} hmac={hmac} />
                  </Tabs.Panel>
                )}
              </Tabs>
            </Form>
          </S.Content>
        </ModalBox>
      )}
    </Formik>
  )
}
